import {
	getRegEdConditionsPending,
	IRegEdSubmissionView,
	useAccountUsesRegEd,
	useRegEdEmailReleaseMutation,
	useRegEdSocialPostReleaseMutation,
} from '@Queries/RegEdCompliance';
import {
	CampaignViewModel,
	EmailSendStatus,
	IEmailMessageFollowUpOptions,
	ScheduleCriteria,
	SendEmailFrom,
} from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'sonner';
import { IImpersonationContextComponentProps, ILocationState, ImpersonationContextKey } from '../../../../models';
import { IUserSessionComponentProps, UserSessionViewModelKey } from '../../../../models/AppState';
import { EmailComposerContext } from '../../../../models/Email';
import { useErrorMessages, useFullscreenModal } from '../../../../models/hooks/appStateHooks';
import { useLocalNotificationService } from '../../../../models/LocalNotifications';
import { Topics } from '../../../../models/LocalNotificationTopics';
import { useEventLogging } from '../../../../models/Logging';
import { getDisplayName } from '../../../../models/NameUtils';
import {
	accountHasComplianceEnabled,
	accountHasComplianceRequired,
	getAccountComplianceEmail,
	getDefaultDateStringValue,
	getSocialMediaPlainTextContentFromRawRichContent,
} from '../../../../models/UiUtils';
import { SocialAccountCampaignViewModel } from '../../../../viewmodels/AdminViewModels';
import {
	ComposeEmailViewModel,
	IOperationResultNoValue,
	PostStatus,
	SocialMediaPostViewModel,
} from '../../../../viewmodels/AppViewModels';
import { Button as ButtonV2 } from '../../../components-v2/Button';
import { baseStyleSheet } from '../../../styles/styles';
import { Button } from '../../Button';
import { ConfirmationDialog, IConfirmationDialogOption } from '../../ConfirmationDialog';
import { buildRegEdEmailReviseLocation } from '../../compliance/regEdReviseEmail';
import { RegEdComplianceStatus } from '../../compliance/RegEdComplianceStatus';
import { TagEmailTagList } from '../../email/TagEmailTagList';
import { ITestEmailResult, TestEmailModal } from '../../email/TestEmailModal';
import { PaperPlaneIcon } from '../../svgs/icons/PaperPlaneIcon';
import { WarningIcon } from '../../svgs/icons/WarningIcon';
import { ApprovalStatus, ComplianceSendActions, ScheduleBy, ScheduleDate } from './presentation';
import { styleSheet } from './styles';

export const lastUsedComplianceRecipientEmail = 'lastUsedComplianceRecipientEmail';
export const getCancelConfirmationOptions = (
	destructiveOptionText: string,
	cancelText = 'Cancel'
): IConfirmationDialogOption<boolean>[] => {
	return [
		{
			isDestructive: true,
			representedObject: true,
			title: destructiveOptionText,
		},
		{
			isCancel: true,
			representedObject: false,
			title: cancelText,
		},
	];
};
interface IProps extends IUserSessionComponentProps, IImpersonationContextComponentProps {
	campaign?: CampaignViewModel | SocialMediaPostViewModel;
	campaignGroup?: CampaignViewModel[];
	className?: string;
	disabled?: boolean;
	emailComposer?: ComposeEmailViewModel<IEmailMessageFollowUpOptions>;
	onApproveClicked?(): void;
	onApproveWithoutCompliance?(): void;
	onCancelClicked?(e?: React.MouseEvent<HTMLElement>): void;
	onChangeScheduleClicked?(): void;
	onEditRecipientsClicked?(): void;
	onViewCitationsClicked?(): void;
	onSendCompliance?(sendWithComplianceEmail?: string): void;
	readOnly?: boolean;
	styles?: StyleDeclarationValue[];
}

const CampaignDetailsSummaryFc: React.FC<IProps> = props => {
	const {
		campaign,
		campaignGroup,
		className,
		disabled,
		emailComposer,
		impersonationContext,
		onApproveClicked,
		onApproveWithoutCompliance,
		onCancelClicked,
		onChangeScheduleClicked,
		onEditRecipientsClicked,
		onSendCompliance,
		onViewCitationsClicked,
		readOnly,
		styles,
		userSession,
	} = props;

	const fullscreenModal = useFullscreenModal();
	const { logApiError, logEvent } = useEventLogging('CampaignDetailsSummary');
	const { postNotification } = useLocalNotificationService<SocialMediaPostViewModel>();
	const { sendTestEmail } = React.useContext(EmailComposerContext) || {};
	const [showingTestEmailModal, setShowingTestEmailModal] = React.useState(false);
	const onTestEmailModalClose = (result?: ITestEmailResult, canceled?: boolean) => {
		setShowingTestEmailModal(false);
		if (result?.success && !canceled) {
			toast.success('Test email sent successfully!');
		}
	};
	const errorMessages = useErrorMessages();
	const [showingCancelConfirmation, setShowingCancelConfirmation] = React.useState<boolean>(false);
	// RegEd accounts suppress the internal approve/reject blocks in favor of the read-only RegEd panel.
	const usesRegEd = useAccountUsesRegEd(impersonationContext);
	const history = useHistory();
	const regEdEmailRelease = useRegEdEmailReleaseMutation({
		onError: error => errorMessages.pushApiError(error),
		onSuccess: () => {
			toast.success('Released — this campaign will send as scheduled.');
			return campaign?.load()?.catch(error => errorMessages.pushApiError(error));
		},
	});
	const regEdSocialRelease = useRegEdSocialPostReleaseMutation({
		onError: error => errorMessages.pushApiError(error),
		onSuccess: () => {
			toast.success('Released — this post will send as scheduled.');
			return campaign?.load()?.catch(error => errorMessages.pushApiError(error));
		},
	});

	if (!campaign) {
		return null;
	}

	// eslint-disable-next-line react-hooks/rules-of-hooks
	const scheduleDate = campaign instanceof CampaignViewModel ? campaign.schedule?.startDate : campaign?.dueDate;
	const renderSendOnBehalf = () => {
		let sendFrom: string;
		if (emailComposer) {
			sendFrom = !emailComposer?.emailMessage?.options?.sendEmailFrom
				? getDisplayName(userSession.user)
				: emailComposer?.emailMessage?.isSendFromOption(SendEmailFrom.SelectedUser) &&
						!!emailComposer.emailMessage.options?.sendEmailFromUser
					? getDisplayName(emailComposer.emailMessage.options.sendEmailFromUser)
					: null;
		} else if (campaign?.actor && campaign.actor.id !== campaign.creator.id) {
			sendFrom = campaign.actor.name;
		}

		if (!sendFrom) {
			return null;
		}
		return (
			<div className={css(styleSheet.card)}>
				<div className={css(styleSheet.cardTitle)}>Send From</div>
				<div>{sendFrom}</div>
			</div>
		);
	};

	const approveCampaign = async () => {
		if (onApproveClicked) {
			onApproveClicked();
			return;
		}
		if (campaign instanceof SocialMediaPostViewModel) {
			try {
				await campaign.approve();

				toast.success(`Social media post approved`);
				postNotification({
					info: campaign,
					topic: Topics.EDIT_SOCIAL_POST,
				});
				fullscreenModal?.dismissModal();
			} catch (error) {
				logApiError('ApproveSocialMediaPost-Error', error);
				errorMessages.pushApiError(error);
			}
		}
	};

	const approveCampaignWithoutCompliance = async () => {
		if (onApproveWithoutCompliance) {
			onApproveWithoutCompliance();
			return;
		}

		if (campaign instanceof SocialMediaPostViewModel) {
			try {
				await campaign.update({ ...campaign.toJs(), sendWithCompliance: false });
				await campaign.approve();
				toast.success(`Social media post approved without compliance`);
				postNotification({
					info: campaign,
					topic: Topics.EDIT_SOCIAL_POST,
				});
				fullscreenModal?.dismissModal();
			} catch (error) {
				logApiError('ApproveSocialMediaPost-Error', error);
				errorMessages.pushApiError(error);
			}
		}
	};

	const cancelClicked = (e?: React.MouseEvent<HTMLElement>) => {
		if (onCancelClicked) {
			onCancelClicked(e);
			return;
		}
		if (campaign instanceof SocialMediaPostViewModel) {
			setShowingCancelConfirmation(true);
		}
	};

	// Recipient is resolved and validated by <ComplianceSendActions>: an explicit typed address, or `null`
	// for the self/client copy so the API routes to its default recipient (the URL is constructed differently
	// for the null case in the underlying send methods).
	const sendCompliance = async (complianceEmail: string | null) => {
		if (onSendCompliance) {
			// Preserve the original recipient semantics: `null` routes to the API default (strictNullChecks is
			// off, so this satisfies the optional `string` parameter).
			onSendCompliance(complianceEmail);
			return;
		}

		if (campaign instanceof SocialMediaPostViewModel) {
			const vm = new SocialAccountCampaignViewModel(userSession, campaign.toJs());
			if (impersonationContext?.isValid) {
				vm.impersonate({
					account: impersonationContext?.account,
					user: campaign.creator,
				});
			}
			try {
				await (vm.sendWithCompliance ? vm.sendComplianceApprovalRequest(complianceEmail) : vm.sendApprovalRequest());
				logEvent('SocialMediaPostNotificationPendingPostCopySend', { complianceEmail });
				toast.success(`Copy sent to ${complianceEmail ? complianceEmail : "client's email address"}`);
			} catch (error) {
				toast.error('Failed to send copy');
				logApiError('SocialMediaPostNotificationPendingPostCopySend-Error', error);
				errorMessages.pushApiError(error);
			}
		}
	};

	const handleReviseCompliance = (submission: IRegEdSubmissionView) => {
		if (!(campaign instanceof CampaignViewModel)) {
			return;
		}
		// This component renders inside the fullscreen modal, whose own Router backs useHistory().
		history.push(buildRegEdEmailReviseLocation(campaign, submission));
	};

	const handleReviseSocialCompliance = (submission: IRegEdSubmissionView) => {
		if (!(campaign instanceof SocialMediaPostViewModel)) {
			return;
		}
		const state: ILocationState<any, any> = {
			model: {
				type: 'Template',
				postContent: getSocialMediaPlainTextContentFromRawRichContent(campaign.content),
				postTargets: campaign.designatedTargets?.length ? campaign.designatedTargets : campaign.targets,
				regEdRevise: {
					revisesApprovalRequestId: submission.approvalRequestId,
					format: submission.metadata?.format,
					answers: submission.metadata?.answers,
				},
			},
		};
		history.push({ pathname: '/social-media/post/create/from-reged-revision', state });
	};

	const regEdConditionsPending = getRegEdConditionsPending(campaign.approvalRequests);

	const handleReleaseConditions = (_: IRegEdSubmissionView) => {
		if (campaign instanceof CampaignViewModel) {
			// A "send now" held campaign persists with no startDate; StartAfter without one is a server 400.
			const schedule = {
				...campaign.schedule,
				criteria: campaign.schedule?.startDate ? ScheduleCriteria.StartAfter : ScheduleCriteria.Immediately,
			};
			regEdEmailRelease.mutate({ campaignId: campaign.id, schedule, impersonationContext });
			return;
		}
		regEdSocialRelease.mutate({ postId: campaign.id, dueDate: campaign.dueDate, impersonationContext });
	};

	const onCancelConfirmationRequestClose = (result?: IConfirmationDialogOption<boolean>, canceled?: boolean) => {
		if (!canceled && result?.representedObject) {
			cancelCampaign();
		}
		setShowingCancelConfirmation(false);
	};

	const cancelCampaign = async () => {
		if (campaign instanceof SocialMediaPostViewModel) {
			try {
				logEvent('CancelSocialCampaign', { id: campaign?.id });

				await campaign.cancel();

				toast.success(campaign?.status === PostStatus.Pending ? `Pending post cancelled` : 'Post cancelled');
				postNotification({
					info: campaign,
					topic: Topics.EDIT_SOCIAL_POST,
				});
				fullscreenModal?.history?.goBack();
			} catch (error) {
				logApiError('CancelSocialPendingCampaign-Error', error);
				errorMessages.pushApiError(error);
			}
		}
	};

	const onRejectionConfirmationRequestClose = (result?: IConfirmationDialogOption<boolean>, canceled?: boolean) => {
		if (campaign instanceof SocialMediaPostViewModel) {
			if (!canceled && !!result?.representedObject) {
				const promise = campaign.reject();
				if (promise) {
					logEvent('RejectCampaign');
					promise
						.then(() => {
							toast.success(`Campaign rejected`);
							postNotification({
								info: campaign,
								topic: Topics.EDIT_SOCIAL_POST,
							});
							fullscreenModal?.dismissModal();
						})
						.catch((error: IOperationResultNoValue) => {
							logApiError(`RejectCampaign-Error`);
							errorMessages.pushApiError(error);
						});
				}
			}
		}
		setShowingCancelConfirmation(false);
	};

	const renderCancelConfirmationDialog = () => (
		<ConfirmationDialog
			icon={<WarningIcon />}
			modalProps={{
				isOpen: showingCancelConfirmation,
				onRequestClose:
					!impersonationContext && campaign?.status === PostStatus.Pending
						? onRejectionConfirmationRequestClose
						: onCancelConfirmationRequestClose,
			}}
			options={getCancelConfirmationOptions(
				!impersonationContext && campaign?.status === PostStatus.Pending ? 'Reject' : 'Cancel Campaign',
				'Nevermind'
			)}
			title={`Are you sure you want to ${
				!impersonationContext && campaign?.status === PostStatus.Pending ? 'reject' : 'cancel'
			} this social media campaign?`}
		/>
	);

	const renderActions = () => {
		if (campaignGroup) {
			return (
				<div className={css(styleSheet.actions, styleSheet.cancelInstructions)}>
					Please go to the Email Reports section to cancel or notify client
				</div>
			);
		}
		if (
			(campaign instanceof CampaignViewModel && !campaign.isScheduledToSend) ||
			(campaign instanceof SocialMediaPostViewModel && !campaign?.hasEditableStatus)
		) {
			return null;
		}
		const showApproveButton =
			!usesRegEd &&
			!(props.impersonationContext?.isValid || accountHasComplianceEnabled(userSession, impersonationContext)) &&
			(campaign instanceof CampaignViewModel ||
				(campaign instanceof SocialMediaPostViewModel && (campaign?.canEdit || campaign?.canEditAsAdmin)));
		return (
			<div
				className={css(
					campaign instanceof CampaignViewModel && campaign.status === EmailSendStatus.WaitingForApproval
						? styleSheet.actions
						: styleSheet.actionsVertical
				)}
			>
				{campaign?.status === EmailSendStatus.Queued && !!sendTestEmail && (
					<ButtonV2 variant='link' onClick={() => setShowingTestEmailModal(true)}>
						<PaperPlaneIcon />
						Send a Test
					</ButtonV2>
				)}
				{(campaign instanceof CampaignViewModel && campaign.status === EmailSendStatus.WaitingForApproval) ||
				(campaign instanceof SocialMediaPostViewModel &&
					campaign.status === PostStatus.Pending &&
					(campaign.canEdit || campaign.canEditAsAdmin || campaign.impersonationContext)) ? (
					<>
						{showApproveButton && (
							<Button
								className={css(baseStyleSheet.ctaButton)}
								disabled={disabled || (campaign.isLoaded && campaign.isBusy)}
								onClick={approveCampaign}
								label='Approve Campaign'
								isLoading={campaign.isLoaded && campaign.isBusy}
							/>
						)}
						<ButtonV2
							variant='destructive-secondary'
							disabled={campaign.isLoaded && campaign.isBusy}
							onClick={cancelClicked}
						>
							{props.impersonationContext?.isValid ? 'Cancel this Campaign' : 'Reject this Campaign'}
						</ButtonV2>
					</>
				) : (campaign instanceof SocialMediaPostViewModel && campaign.hasEditableStatus) ||
					(campaign instanceof CampaignViewModel && !readOnly) ? (
					<Button
						className={css(baseStyleSheet.linkDestructive)}
						disabled={campaign.isLoaded && campaign.isBusy}
						onClick={cancelClicked}
						label='Cancel Campaign'
						kind='custom'
					/>
				) : null}
			</div>
		);
	};

	const IsSendOnBehalfFromUser =
		campaign instanceof SocialMediaPostViewModel &&
		campaign?.actor &&
		campaign.actor.id !== campaign.creator.id &&
		!(campaign?.actor.isLevitateSupport && campaign?.approvalRequests);

	const emailComplianceActions =
		campaign instanceof CampaignViewModel &&
		campaign.status === EmailSendStatus.WaitingForApproval &&
		accountHasComplianceEnabled(userSession, impersonationContext) &&
		emailComposer.emailMessage.options?.sendWithCompliance === true;

	const socialComplianceActions =
		campaign instanceof SocialMediaPostViewModel &&
		campaign.status === PostStatus.Pending &&
		(campaign?.canEdit || campaign?.canEditAsAdmin || props.impersonationContext?.isValid) &&
		accountHasComplianceEnabled(userSession, impersonationContext);

	const complianceRequired = accountHasComplianceRequired(userSession, impersonationContext);

	return (
		<div
			className={`${css(
				campaign instanceof CampaignViewModel ? styleSheet.container : styleSheet.socialContainer,
				...(styles || [])
			)} campaign-details-summary ${className || ''}`}
		>
			{!!sendTestEmail && (
				<TestEmailModal
					isOpen={showingTestEmailModal}
					onRequestClose={onTestEmailModalClose}
					onSendTestEmail={sendTestEmail}
				/>
			)}
			{campaign instanceof CampaignViewModel ? (
				<>
					{renderSendOnBehalf()}
					<div className={css(styleSheet.card, styleSheet.recipientsCard)}>
						<div className={css(styleSheet.cardTitle)}>Recipients</div>
						<div className={css(styleSheet.cardActions)}>
							<div className={css(baseStyleSheet.truncateText)}>
								{`Approximately ${
									!campaignGroup ? campaign.totalCount : campaignGroup.reduce((x, camp) => x + camp.totalCount, 0)
								}`}
							</div>
							{!readOnly && !campaignGroup && (
								<button className={css(baseStyleSheet.brandLink)} onClick={onEditRecipientsClicked}>
									<span>{`Preview${!impersonationContext?.isValid && !emailComposer?.isFollowUp ? '/Edit' : ''}`}</span>
								</button>
							)}
						</div>
						<TagEmailTagList
							campaign={campaign}
							emailComposer={emailComposer}
							readOnly={true}
							styles={[styleSheet.tagList]}
						/>
					</div>
				</>
			) : null}
			{scheduleDate ? (
				<>
					<ScheduleDate
						title={campaign instanceof CampaignViewModel ? 'Send Time' : 'Posting Date'}
						scheduleDate={scheduleDate}
						showChangeButton={
							(campaign instanceof CampaignViewModel &&
								campaign?.isScheduledToSend &&
								!campaignGroup &&
								!emailComposer?.editModeIsActive) ||
							(campaign instanceof SocialMediaPostViewModel &&
								(campaign?.canEdit || (campaign?.impersonationContext && campaign?.hasEditableStatus)))
						}
						disabled={disabled}
						onClick={onChangeScheduleClicked}
					/>
				</>
			) : null}
			{IsSendOnBehalfFromUser ? <ScheduleBy name={campaign?.actor?.name} /> : null}
			{usesRegEd ? (
				<RegEdComplianceStatus
					parentId={campaign.id}
					impersonationContext={impersonationContext}
					onRevise={campaign instanceof CampaignViewModel ? handleReviseCompliance : handleReviseSocialCompliance}
					conditionsPending={regEdConditionsPending}
					onReleaseConditions={handleReleaseConditions}
					isReleasing={regEdEmailRelease.isPending || regEdSocialRelease.isPending}
				/>
			) : null}
			{!usesRegEd && (emailComplianceActions || socialComplianceActions) && (
				<ComplianceSendActions
					accountComplianceEmail={getAccountComplianceEmail(userSession, impersonationContext)}
					isImpersonating={!!props.impersonationContext?.isValid}
					selfEmail={userSession.user.email}
					disabled={disabled}
					onSend={sendCompliance}
					// Email hides the send actions while editing or for grouped/unscheduled campaigns; social
					// always shows them. Coerce to a real boolean so the component default never sneaks in.
					showSendActions={
						!!(
							(emailComplianceActions &&
								campaign.isScheduledToSend &&
								!campaignGroup &&
								!emailComposer.editModeIsActive) ||
							socialComplianceActions
						)
					}
					footer={
						!props.impersonationContext?.isValid ? (
							<div className={css(styleSheet.buttonActions)}>
								<div className={css(baseStyleSheet.horizontalStack)}>
									{emailComposer?.campaign?.emailBodyTemplate?.citation?.length > 0 && (
										<button className={css(baseStyleSheet.ctaButtonReverse)} onClick={onViewCitationsClicked}>
											<span>View Citations</span>
										</button>
									)}
									<button className={css(baseStyleSheet.ctaButton)} disabled={disabled} onClick={approveCampaign}>
										<span>Mark as Approved</span>
									</button>
									{complianceRequired || (socialComplianceActions && !campaign?.canEdit) ? null : (
										<button
											className={css(baseStyleSheet.ctaButtonReverse)}
											disabled={disabled}
											onClick={approveCampaignWithoutCompliance}
										>
											<span>{`${emailComplianceActions ? 'Send Anyway' : 'Post Anyway'} `}</span>
										</button>
									)}
								</div>
							</div>
						) : null
					}
				/>
			)}
			{!usesRegEd &&
			campaign instanceof SocialMediaPostViewModel &&
			campaign.status === PostStatus.Pending &&
			!socialComplianceActions &&
			campaign?.approvalRequests?.length > 0 ? (
				<ApprovalStatus title='Pending Approval' />
			) : null}
			{!usesRegEd &&
				campaign instanceof CampaignViewModel &&
				campaign.status === EmailSendStatus.Queued &&
				accountHasComplianceEnabled(userSession, impersonationContext) &&
				campaign.approvalRequests?.length > 0 && (
					<div className={css(styleSheet.card)}>
						<div className={css(styleSheet.cardTitle)}>Compliance Approval</div>
						<div className={css(styleSheet.cardActions)}>
							{emailComposer.emailMessage.options?.sendWithCompliance ? (
								<div className={css(styleSheet.cardActions)}>
									<div className={css(styleSheet.approvedStatusTitle)}>Approved &nbsp;</div>
									<div className={css(styleSheet.approvedStatusDate)}>
										{getDefaultDateStringValue(campaign.approvalRequests[0].lastModifiedDate)}
									</div>
								</div>
							) : (
								<div className={css(styleSheet.approvedStatusTitle)}>
									{'Sent By ' + campaign.approvalRequests[0]?.approver?.firstName + ' Anyway'}
								</div>
							)}
						</div>
					</div>
				)}
			{!usesRegEd &&
				campaign instanceof SocialMediaPostViewModel &&
				(campaign.status === PostStatus.Scheduled ||
					campaign.status === PostStatus.Succeeded ||
					campaign.status === PostStatus.PartiallySucceeded) &&
				accountHasComplianceEnabled(userSession, impersonationContext) && (
					<div className={css(styleSheet.card)}>
						<div className={css(styleSheet.cardTitle)}>Compliance Approval</div>
						<div className={css(styleSheet.cardActions)}>
							{campaign?.sendWithCompliance ? (
								<div className={css(styleSheet.cardActions)}>
									<div className={css(styleSheet.approvedStatusTitle)}>Approved &nbsp;</div>
									<div className={css(styleSheet.approvedStatusDate)}>
										{campaign?.approvalRequests &&
											getDefaultDateStringValue(campaign?.approvalRequests[0]?.lastModifiedDate)}
									</div>
								</div>
							) : campaign?.approvalRequests?.length > 0 ? (
								<div className={css(styleSheet.approvedStatusTitle)}>
									{'Scheduled By ' + campaign?.approvalRequests[0].approver?.firstName + ' Anyway'}
								</div>
							) : (
								<div className={css(styleSheet.approvedStatusTitle)}>Scheduled Without Compliance</div>
							)}
						</div>
					</div>
				)}

			{renderActions()}
			{renderCancelConfirmationDialog()}
		</div>
	);
};

const CampaignDetailsSummaryAsObserver = observer(CampaignDetailsSummaryFc);
export const CampaignDetailsSummary = inject(
	UserSessionViewModelKey,
	ImpersonationContextKey
)(CampaignDetailsSummaryAsObserver);
